import { PlacementId } from '../placementIds'
import {
  maxItemsOfType,
  maxItemsOfTypeAcross,
  maxItemsOfTypesPerPlacement,
  maxItemsPerPlacement,
  typesMayNotShareAPlacement,
} from '../rules'
import { RuleCheck } from '../types'

/**
 * DTC's rulebook — the multi-placement policy specified by LFE-124.
 *
 * Rules:
 * - Maximum 1 icon across all placements on the garment
 * - Maximum 1 logo across all placements on the garment
 * - Chest has two spots — right and left — and each spot can hold up to 2 embroidery items (e.g., Icon + Text, or Logo + Text)
 * - Icon and logo can both appear on the same garment — they just can't both be in the same single placement
 * - Text may appear on at most one sleeve
 * - Logo + Text is never allowed on a sleeve — logo cannot sit on top of sleeve text
 * - Icon + Text is allowed on a sleeve — icon may sit on top of sleeve text
 * - Once the garment's single icon (or single logo) has been used anywhere, that same type is blocked everywhere else
 */

const SLEEVES: readonly PlacementId[] = ['SLEEVE:LEFT', 'SLEEVE:RIGHT']

export const MAX_TEXT_ITEMS = 3
const MAX_ICON_ITEMS = 1
const MAX_LOGO_ITEMS = 1
const MAX_ITEMS_PER_PLACEMENT = 2
const MAX_SLEEVE_TEXT_ITEMS = 1
const MAX_MARKS_PER_PLACEMENT = 1
const MAX_TEXT_ITEMS_PER_PLACEMENT = 1

/**
 * There is deliberately no garment-wide total cap: the largest legal selection is 5 items
 * (logo + text on one chest, text on the other chest, icon + text on one sleeve).
 *
 * Icon + text may share a placement — legal by the *absence* of a rule. Text never stacks on
 * itself (`one-text-per-placement`): a multi-line monogram is one item carrying several lines via
 * `limits.lineLimit`, not two items on the same spot.
 */
export const dtcEmbroideryRules: readonly RuleCheck[] = [
  maxItemsOfType('max-text-items', 'text', MAX_TEXT_ITEMS),
  maxItemsOfType('max-icon-items', 'icon', MAX_ICON_ITEMS),
  maxItemsOfType('max-logo-items', 'logo', MAX_LOGO_ITEMS),
  maxItemsPerPlacement('position-capacity', MAX_ITEMS_PER_PLACEMENT),
  maxItemsOfTypeAcross('sleeve-text', 'text', SLEEVES, MAX_SLEEVE_TEXT_ITEMS),
  maxItemsOfTypesPerPlacement('one-text-per-placement', ['text'], MAX_TEXT_ITEMS_PER_PLACEMENT),
  maxItemsOfTypesPerPlacement('one-mark-per-placement', ['icon', 'logo'], MAX_MARKS_PER_PLACEMENT),
  typesMayNotShareAPlacement('no-logo-over-sleeve-text', 'logo', 'text', SLEEVES),
]
